import React, { useEffect, useState, useCallback } from "react";
import { Wallet, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const POLL_MS = 30000;

// Live paid headcount — same rule as the Accounts tab (receipts, bulk = every delegate).
export const PaidCounter = ({ eventId, refreshKey }) => {
  const [s, setS] = useState(null);
  const [spin, setSpin] = useState(false);

  const fetchIt = useCallback(async () => {
    setSpin(true);
    try { const r = await api.get(`/events/${eventId}/paid-summary`); setS(r.data); } catch { /* keep last value */ }
    setSpin(false);
  }, [eventId]);

  useEffect(() => { fetchIt(); }, [fetchIt, refreshKey]);
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") fetchIt(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", tick); };
  }, [fetchIt]);

  const gap = s ? s.paid_students - s.allotments_marked_paid : 0;
  return (
    <div className="dashboard-card p-4 mb-4 flex flex-wrap items-center gap-x-8 gap-y-3 border-l-4 border-emerald-500" data-testid="paid-counter">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center"><Wallet className="w-5 h-5 text-emerald-700" /></div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Students paid (as per Accounts)</div>
          <div className="font-display font-black text-3xl text-emerald-700 leading-none mt-0.5" data-testid="paid-counter-value">{s ? s.paid_students : "—"}</div>
        </div>
      </div>
      <Stat label="Receipts" value={s?.receipts} testid="paid-counter-receipts" />
      <Stat label="Allotted" value={s?.allotments_total} testid="paid-counter-allotted" />
      <Stat label="Marked paid in allotments" value={s?.allotments_marked_paid} testid="paid-counter-marked" />
      {s && gap !== 0 && (
        <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${gap > 0 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"}`} data-testid="paid-counter-gap">
          {gap > 0 ? `${gap} paid but not marked paid in allotments` : `${-gap} marked paid without a receipt`}
        </span>
      )}
      <button onClick={fetchIt} className="ml-auto text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="paid-counter-refresh" title="Auto-updates every 30 seconds">
        <RefreshCw className={`w-3.5 h-3.5 ${spin ? "animate-spin" : ""}`} /> Live · {s ? new Date(s.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "…"}
      </button>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div>
    <div className="text-xs text-slate-500">{label}</div>
    <div className="font-bold text-lg text-slate-900" data-testid={testid}>{value ?? "—"}</div>
  </div>
);
